import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { getNearestStation, getStation, getStations } from "../services/stationService";
import { Station } from "../types/station";
import { getFile, getFileMetadata, getFileId } from "../services/fileService";
type StationContextType = {
    station: Station;
    stations: Array<Station>;
    setStation: (station: Station) => void;
    fetchStation: (stationId: string) => void;
    fetchNearestStation: (latitude: number, longitude: number) => void;
    fetchStations: () => void;
    fetchMyNearestStation: () => Promise<Station>;
    setMyStation: (station: Station) => void;
}

export const StationContext = createContext<StationContextType>({ station: {} as Station, stations: [], setStation: () => { }, fetchStation: () => { }, fetchNearestStation: () => { }, fetchStations: () => { }, fetchMyNearestStation: () => { } });

export const StationProvider = ({ children }: { children: ReactNode }) => {
    const [station, setStation] = useState<Station>(() => JSON.parse(localStorage.getItem('station') || '{}') as Station);
    const [stations, setStations] = useState<Array<Station>>([]);

    useEffect(() => {
        fetchStations();
    }, []);

    const fetchStations = async () => {
        getStations().then(response => {
            setStations(response.data);
        }).catch((error) => {
            console.error('Error fetching stations:', error);
            throw error;
        });
    }

    const fetchStation = async (stationId: string) => {
        if (stationId === "0") return;

        getStation(stationId).then(response => {
            handleStationResponse(response.data);
        }).catch(error => {
            console.error('Error fetching station:', error);
        });
    }

    const fetchNearestStation = async (latitude: number, longitude: number) => {
        getNearestStation(latitude, longitude).then(response => {
            handleStationResponse(response.data);
        }).catch(error => {
            console.error('Error fetching nearest station:', error);
        });
    }
    const fetchMyNearestStation = async () => {
        return new Promise<Station>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(position => {
                getNearestStation(position.coords.latitude, position.coords.longitude).then(response => {
                    resolve(response.data);
                }).catch(error => {
                    reject(error);
                });
            }, () => {
                getNearestStation(28.679079, 77.069710).then(response => {
                    resolve(response.data);
                }).catch(error => {
                    reject(error);
                });
            });
        });
    }


    const handleStationResponse = async(response: Station) => {
        try {
            const fileId = getFileId(response.logoUrl);
            const metadata = await getFileMetadata(fileId);
            response.logoAlt = metadata.AltName || response.name;
        } catch (error) {
            console.error('Error fetching logo alt text:', error);
        }
        localStorage.setItem('station', JSON.stringify({ ...response, logo: getFile(response.logoUrl) }));
        setStation({ ...response, logo: getFile(response.logoUrl) });
    }

    const setMyStation = async (station: Station) => {
        localStorage.setItem('station', JSON.stringify({ ...station, logo: getFile(station.logoUrl) }));
        try {
            const fileId = getFileId(station.logoUrl);
            const metadata = await getFileMetadata(fileId);
            station.logoAlt = metadata.AltName || station.name;
        } catch (error) {
            console.error('Error fetching logo alt text:', error);
        }
        setStation({ ...station, logo: getFile(station.logoUrl) });
    }

    return <StationContext.Provider value={{ station, stations, setStation, fetchStation, fetchNearestStation, fetchStations, fetchMyNearestStation, setMyStation }}>{children}</StationContext.Provider>;
}

export const useStation = () => {
    const context = useContext(StationContext);
    if (!context) {
        throw new Error('useStation must be used within a StationProvider');
    }
    return context;
}